// Tabela rejestru „jak w Excelu” (SR-014): edycja w komórkach, wklejanie z Excela, import pliku, wzory.
// Wspólna dla rejestru akcji raportu (ActionLogGrid) i rejestru ośrodka (SiteIssuesGrid); różni je źródło danych.
// Styl react-data-grid ładuje public/index.html (kopia w public/vendor), nie import CSS: minifikator CRA psuł @layer.
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import DataGrid, { Column, RenderEditCellProps, textEditor } from "react-data-grid";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import type { SpreadsheetTable } from "../../../api/reportsApi";
import { downloadBlob, downloadCSV } from "../../../utils/exportUtils";
import { getTemplateProblems } from "../../../api/questionsApi";
import { ActionStatus } from "../../../types/reportActions";
import { TrashIcon } from "../../../components/atomic/discussionComponent";
import { Button } from "../../../components/ui/button";
import { btn, cx } from "../../../components/ui/styles";
import {
  ActionLogRow,
  Category,
  FORMAT_COLUMNS,
  ParsedImport,
  PastedRow,
  RowErrors,
  RowField,
  ValidationRules,
  emptyRow,
  formatDatePl,
  parseDatePl,
  rowsFromClipboard,
  toIsoDate,
  validateRow,
} from "./actionLogFormat";
import { ActionLogPasteModal } from "./ActionLogPasteModal";
import { importActionLogFile } from "./actionLogFile";

export const today = () => toIsoDate(new Date());
let tmpCounter = 0;
const newKey = () => `nowy-${Date.now()}-${++tmpCounter}`;

// Zmiana statusu ustawia/czyści datę zamknięcia (jak w dotychczasowym formularzu)
const applyStatusRules = (r: ActionLogRow): ActionLogRow => {
  if (r.status === ActionStatus.Closed && !r.closedDate) return { ...r, closedDate: today() };
  if (r.status === ActionStatus.Open && r.closedDate) return { ...r, closedDate: null };
  return r;
};

/* ----- edytory komórek ----- */

const editorBase =
  "w-full h-full px-2 text-sm border-2 border-[var(--sc-amber)] outline-none bg-white text-[var(--sc-text)]";

const LongTextEditor = ({ row, column, onRowChange, onClose }: RenderEditCellProps<ActionLogRow>) => {
  const field = column.key as RowField;
  return (
    <textarea
      autoFocus
      className={cx(editorBase, "absolute left-0 top-0 z-10 min-h-[140px] py-1.5 shadow-lg resize-y")}
      value={String(row[field] ?? "")}
      onChange={(e) => onRowChange({ ...row, [field]: e.target.value })}
      onBlur={() => onClose(true)}
      onKeyDown={(e) => {
        // Enter = nowa linia (jak Alt+Enter w Excelu), Ctrl/Cmd+Enter lub Tab = zatwierdź
        if ((e.key === "Enter" && (e.ctrlKey || e.metaKey)) || e.key === "Tab") {
          e.preventDefault();
          onClose(true);
        } else if (e.key === "Escape") onClose(false);
        e.stopPropagation();
      }}
    />
  );
};

const DateEditor = ({ row, column, onRowChange, onClose }: RenderEditCellProps<ActionLogRow>) => {
  const field = column.key as "identificationDate" | "closedDate";
  const [text, setText] = useState(formatDatePl(row[field]));
  const parsed = parseDatePl(text);
  const invalid = text.trim() !== "" && !parsed;
  const commit = () => {
    if (invalid) return;
    onRowChange({ ...row, [field]: parsed }, true);
  };
  return (
    <input
      autoFocus
      className={cx(editorBase, invalid && "border-[#C03030]")}
      placeholder="DD.MM.RRRR"
      title={invalid ? "Wpisz datę jako DD.MM.RRRR" : undefined}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => (invalid ? onClose(false) : commit())}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === "Tab") {
          if (invalid) { e.preventDefault(); e.stopPropagation(); return; }
          commit();
        }
      }}
    />
  );
};

const selectEditor =
  (options: { value: string; label: string }[], field: "categoryId" | "status") =>
  ({ row, onRowChange, onClose }: RenderEditCellProps<ActionLogRow>) => (
    <select
      autoFocus
      className={editorBase}
      value={row[field] === null ? "" : String(row[field])}
      onChange={(e) => {
        const v = e.target.value;
        onRowChange({ ...row, [field]: v === "" ? null : Number(v) }, true);
      }}
      onBlur={() => onClose(false)}
    >
      {field === "categoryId" && <option value="">—</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );

/* ----- komponent ----- */

// Źródło wierszy i zapis: akcje raportu albo rejestr ośrodka
export type IssueLogSource = {
  rows: ActionLogRow[] | undefined; // w kolejności wyświetlania
  isLoading: boolean;
  error: unknown;
  save: (row: ActionLogRow) => Promise<string | undefined>; // dla nowego wiersza zwraca id
  remove: (id: string) => Promise<unknown>;
  refresh: () => void;
  parseXlsx: (file: File) => Promise<SpreadsheetTable>;
  downloadTemplate: () => Promise<Blob>;
  downloadData: () => Promise<Blob>;
};

export type IssueLogGridProps = {
  source: IssueLogSource;
  title: string;
  description?: ReactNode;
  rules: ValidationRules;
  dataExport: { label: string; hint: string; fileName: string };
  headers?: Partial<Record<RowField, string>>; // nagłówki inne niż w arkuszu klienta
  renderRowActions?: (row: ActionLogRow) => ReactNode; // np. komentarze, historia
  selection?: { selected: Set<string>; onChange: (selected: Set<string>) => void };
  toolbar?: ReactNode; // dodatkowe przyciski przed „Pobierz wzór”
  readOnly?: boolean; // bez edycji, dodawania, importu i usuwania (np. raport zaakceptowany: migawka)
};

export const IssueLogGrid = ({
  source, title, description, rules, dataExport, headers, renderRowActions, selection, toolbar, readOnly = false,
}: IssueLogGridProps) => {
  const [rows, setRows] = useState<ActionLogRow[]>([]);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const [pasted, setPasted] = useState<ParsedImport | null>(null);
  const [importTitle, setImportTitle] = useState<string>();
  const [reading, setReading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [templateMenu, setTemplateMenu] = useState(false);

  const downloadTemplate = async (kind: "xlsx" | "csv" | "data") => {
    setTemplateMenu(false);
    if (kind === "csv") {
      downloadCSV([["lp.", ...FORMAT_COLUMNS.map((c) => c.header)]], "Wzor_rejestru_akcji.csv", ";");
      return;
    }
    try {
      const blob = await (kind === "data" ? source.downloadData() : source.downloadTemplate());
      downloadBlob(blob, kind === "data" ? `${dataExport.fileName}_${today()}.xlsx` : "Wzor_rejestru_akcji.xlsx");
    } catch {
      toast.error("Nie udało się pobrać pliku.");
    }
  };
  // Błędy z wklejenia (np. nieznana kategoria) trzymane przy wierszu, aż monitor poprawi tę komórkę
  const [pasteErrors, setPasteErrors] = useState<Map<string, RowErrors>>(new Map());
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data: dictionary } = useQuery(["problemsDictionary"], getTemplateProblems, { staleTime: 10 * 60 * 1000 });
  // puste nazwy w słowniku (są takie na produkcji) pomijamy na liście wyboru
  const categories: Category[] = useMemo(() => (dictionary?.categories ?? []).filter((c) => c.value?.trim()), [dictionary]);

  // Wiersze z serwera + niezapisane nowe wiersze (drafty) na końcu
  useEffect(() => {
    if (!source.rows) return;
    const server = source.rows;
    setRows((prev) => [...server, ...prev.filter((r) => !r.id)]);
  }, [source.rows]);

  const saveRow = async (row: ActionLogRow) => {
    setSaving((s) => new Set(s).add(row.key));
    try {
      const id = await source.save(row);
      if (!row.id) {
        // draft staje się zwykłym wierszem; odświeżenie z serwera go podmieni
        setRows((prev) => prev.map((r) => (r.key === row.key ? { ...r, id, key: id ?? r.key } : r)));
      }
      source.refresh();
    } catch (e: any) {
      toast.error(`Nie zapisano wiersza: ${e?.response?.data?.message ?? e?.message ?? "błąd serwera"}`);
    } finally {
      setSaving((s) => { const n = new Set(s); n.delete(row.key); return n; });
    }
  };

  const removeRow = async (id: string) => {
    try {
      await source.remove(id);
      source.refresh();
    } catch (e: any) {
      toast.error(`Nie usunięto: ${e?.message ?? "błąd serwera"}`);
    }
  };

  const errorsFor = (row: ActionLogRow, extra?: RowErrors): RowErrors => ({ ...(extra ?? pasteErrors.get(row.key)), ...validateRow(row, rules) });

  const onRowsChange = (next: ActionLogRow[], { indexes, column }: { indexes: number[]; column: { key: string } }) => {
    const updated = next.map((r, i) => (indexes.includes(i) ? applyStatusRules(r) : r));
    setRows(updated);
    setTouched((t) => { const n = new Set(t); indexes.forEach((i) => n.add(updated[i].key)); return n; });
    const remaining = new Map(pasteErrors);
    indexes.forEach((i) => {
      const key = updated[i].key;
      const left = { ...remaining.get(key) };
      delete left[column.key as RowField];
      if (Object.keys(left).length) remaining.set(key, left); else remaining.delete(key);
    });
    setPasteErrors(remaining);
    indexes.forEach((i) => {
      const row = updated[i];
      if (Object.keys(errorsFor(row, remaining.get(row.key))).length === 0) saveRow(row);
    });
  };

  const addDraft = () => {
    const key = newKey();
    setRows((prev) => [...prev, emptyRow(key, today())]);
  };

  const deleteRowAt = (row: ActionLogRow) => {
    if (!row.id) {
      setRows((prev) => prev.filter((r) => r.key !== row.key));
      return;
    }
    if (window.confirm("Usunąć tę pozycję z rejestru?")) removeRow(row.id);
  };

  // Wklejanie z Excela: przechwytujemy, gdy schowek ma tabulatory (kilka komórek) albo gdy
  // fokus nie jest w polu edycji. Zwykłe wklejanie tekstu do edytowanej komórki zostaje bez zmian.
  const onPaste = (e: React.ClipboardEvent) => {
    if (readOnly) return;
    const text = e.clipboardData.getData("text/plain");
    const target = e.target as HTMLElement;
    const inEditor = target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT";
    if (!text || (inEditor && !text.includes("\t"))) return;
    e.preventDefault();
    setImportTitle(undefined);
    setPasted(rowsFromClipboard(text, categories, newKey, today(), rules));
  };

  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // ten sam plik można wybrać ponownie po poprawkach
    if (!file) return;
    setReading(true);
    try {
      const parsed = await importActionLogFile(file, categories, newKey, today(), source.parseXlsx, rules);
      setImportTitle(`Import z pliku „${file.name}”`);
      setPasted(parsed);
    } finally {
      setReading(false);
    }
  };

  const importPasted = async (selected: PastedRow[]) => {
    setPasted(null);
    const withErrors = selected.filter((p) => Object.keys(p.errors).length > 0);
    setRows((prev) => [...prev, ...selected.map((p) => p.row)]);
    setTouched((t) => { const n = new Set(t); withErrors.forEach((p) => n.add(p.row.key)); return n; });
    setPasteErrors((m) => { const n = new Map(m); withErrors.forEach((p) => n.set(p.row.key, p.errors)); return n; });
    for (const p of selected) {
      if (Object.keys(p.errors).length === 0) await saveRow(p.row);
    }
  };

  const errorsByKey = useMemo(() => {
    const m = new Map<string, ReturnType<typeof validateRow>>();
    rows.forEach((r) => { if (touched.has(r.key)) m.set(r.key, { ...pasteErrors.get(r.key), ...validateRow(r, rules) }); });
    return m;
  }, [rows, touched, pasteErrors, rules]);

  const savedIds = rows.filter((r) => r.id).map((r) => r.id!);
  const allSelected = !!selection && savedIds.length > 0 && savedIds.every((id) => selection.selected.has(id));

  const columns = useMemo((): Column<ActionLogRow>[] => {
    const categoryName = (id: number | null) => categories.find((c) => c.id === id)?.value ?? "";
    const errorOf = (row: ActionLogRow, field: RowField) => errorsByKey.get(row.key)?.[field];
    const cellClass = (field: RowField) => (row: ActionLogRow) =>
      errorOf(row, field) ? "bg-[#FDECEC] !border-[#C03030]" : undefined;
    const text = (field: RowField, value: string) => ({ row }: { row: ActionLogRow }) => (
      <span title={errorOf(row, field) ?? value} className="block truncate">{value}</span>
    );
    // Nagłówki jak w arkuszu klienta, zawijane w dwie linie zamiast ucinania
    const header = (field: RowField) => {
      const full = headers?.[field] ?? FORMAT_COLUMNS.find((c) => c.field === field)!.header;
      return <span title={full} className="block whitespace-normal leading-tight text-xs font-semibold py-1">{full}</span>;
    };
    const yesNo = (field: "potentialDeviation" | "potentialSeriousFinding" | "seriousBreach"): Column<ActionLogRow> => ({
      key: field,
      name: header(field),
      width: 110,
      renderCell: ({ row, onRowChange }) => (
        <label className="flex items-center gap-1.5 h-full cursor-pointer text-xs">
          <input
            type="checkbox"
            checked={row[field]}
            disabled={readOnly}
            onChange={(e) => onRowChange({ ...row, [field]: e.target.checked })}
          />
          {row[field] ? <span className="font-medium text-[#C43A3A]">TAK</span> : <span className="text-[var(--sc-muted)]">NIE</span>}
        </label>
      ),
    });
    const toggle = (id: string, on: boolean) => {
      if (!selection) return;
      const n = new Set(selection.selected);
      if (on) n.add(id); else n.delete(id);
      selection.onChange(n);
    };

    const selectColumn: Column<ActionLogRow>[] = selection ? [{
      key: "select", name: (
        <input
          type="checkbox"
          aria-label="Zaznacz wszystkie"
          checked={allSelected}
          onChange={(e) => selection.onChange(e.target.checked ? new Set(savedIds) : new Set())}
        />
      ),
      width: 36, frozen: true, resizable: false,
      renderCell: ({ row }) => row.id ? (
        <input
          type="checkbox"
          aria-label="Zaznacz"
          checked={selection.selected.has(row.id)}
          onChange={(e) => toggle(row.id!, e.target.checked)}
        />
      ) : null,
    }] : [];

    const all: Column<ActionLogRow>[] = [
      ...selectColumn,
      {
        key: "lp", name: "lp.", width: 44, frozen: true,
        renderCell: ({ row, rowIdx }) => row.needsReview
          ? <span title={row.reviewNote ?? "Do weryfikacji"} className="font-semibold text-[#C07810]">{rowIdx + 1}!</span>
          : <span className="text-[var(--sc-muted)]">{rowIdx + 1}</span>,
      },
      { key: "participantNumber", name: header("participantNumber"), width: 150, frozen: true, renderEditCell: textEditor, cellClass: cellClass("participantNumber"), renderCell: ({ row }) => text("participantNumber", row.participantNumber)({ row }) },
      { key: "visitNumber", name: header("visitNumber"), width: 120, renderEditCell: textEditor, renderCell: ({ row }) => text("visitNumber", row.visitNumber)({ row }) },
      {
        key: "categoryId", name: header("categoryId"), width: 150, cellClass: cellClass("categoryId"),
        renderEditCell: selectEditor(categories.map((c) => ({ value: String(c.id), label: c.value })), "categoryId"),
        renderCell: ({ row }) => text("categoryId", categoryName(row.categoryId))({ row }),
      },
      { key: "deviationDescription", name: header("deviationDescription"), width: 280, cellClass: cellClass("deviationDescription"), renderEditCell: LongTextEditor, editorOptions: { displayCellContent: true }, renderCell: ({ row }) => text("deviationDescription", row.deviationDescription)({ row }) },
      { key: "identificationDate", name: header("identificationDate"), width: 120, cellClass: cellClass("identificationDate"), renderEditCell: DateEditor, renderCell: ({ row }) => text("identificationDate", formatDatePl(row.identificationDate))({ row }) },
      { key: "closedDate", name: header("closedDate"), width: 120, cellClass: cellClass("closedDate"), renderEditCell: DateEditor, renderCell: ({ row }) => text("closedDate", formatDatePl(row.closedDate))({ row }) },
      {
        key: "status", name: header("status"), width: 120,
        renderEditCell: selectEditor([{ value: String(ActionStatus.Open), label: "otwarte" }, { value: String(ActionStatus.Closed), label: "zamknięte" }], "status"),
        renderCell: ({ row }) => (
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${row.status === ActionStatus.Open ? "bg-[#FFF8ED] text-[#C47A0A]" : "bg-[#EDFAF3] text-[#1E8A55]"}`}>
            {row.status === ActionStatus.Open ? "otwarte" : "zamknięte"}
          </span>
        ),
      },
      { key: "actionsTaken", name: header("actionsTaken"), width: 260, cellClass: cellClass("actionsTaken"), renderEditCell: LongTextEditor, editorOptions: { displayCellContent: true }, renderCell: ({ row }) => text("actionsTaken", row.actionsTaken)({ row }) },
      { key: "responsiblePerson", name: header("responsiblePerson"), width: 180, renderEditCell: textEditor, renderCell: ({ row }) => text("responsiblePerson", row.responsiblePerson)({ row }) },
      yesNo("potentialDeviation"),
      yesNo("potentialSeriousFinding"),
      yesNo("seriousBreach"),
      {
        key: "actions", name: "", width: 136, resizable: false,
        renderCell: ({ row }) => (
          <div className="flex items-center gap-1 h-full">
            {saving.has(row.key) && <span className="text-[11px] text-[var(--sc-muted)]">zapis…</span>}
            {row.id && !saving.has(row.key) && renderRowActions?.(row)}
            {!readOnly && <button
              type="button"
              onClick={() => deleteRowAt(row)}
              className={cx(btn.icon, "text-[var(--sc-muted)] hover:bg-[#FFF3F3] hover:text-[#C03030]")}
              aria-label="Usuń"
              title="Usuń"
            >
              <TrashIcon className="h-4 w-4" />
            </button>}
          </div>
        ),
      },
    ];
    // tylko do odczytu: bez edytorów komórek
    return readOnly ? all.map(({ renderEditCell, ...c }) => c) : all;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, errorsByKey, saving, selection?.selected, allSelected, headers, readOnly]);

  const invalidDrafts = rows.filter((r) => !r.id && Object.keys(errorsByKey.get(r.key) ?? {}).length > 0).length;

  return (
    <div className="bg-[var(--sc-surface)] border border-[var(--sc-border)] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--sc-border)]">
        <div>
          <h3 className="text-sm font-semibold text-[var(--sc-text)]">{title}</h3>
          <p className="text-xs text-[var(--sc-muted)]">
            {description ?? (
              <>
                Kliknij dwukrotnie komórkę albo zacznij pisać, żeby edytować. Wiersze z Excela wklejasz przez Ctrl+V / Cmd+V
                albo wgrywasz plik (.xlsx, CSV) w formacie wzoru.
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {toolbar}
          {!readOnly && <>
          <input ref={fileInputRef} type="file" accept=".xlsx,.csv,.txt" className="hidden" onChange={onFileChosen} />
          <div className="relative">
            <Button size="sm" variant="secondary" onClick={() => setTemplateMenu((v) => !v)} aria-expanded={templateMenu}>
              Pobierz wzór ▾
            </Button>
            {templateMenu && (
              <div
                className="absolute right-0 z-20 mt-1 w-64 rounded-lg border border-[var(--sc-border)] bg-[var(--sc-surface)] py-1 shadow-lg text-sm"
                onMouseLeave={() => setTemplateMenu(false)}
              >
                {([
                  ["xlsx", "Wzór Excel (.xlsx)", "z listami rozwijanymi i podpowiedziami"],
                  ["csv", "Wzór CSV", "sam nagłówek, separator ;"],
                  ["data", dataExport.label, dataExport.hint],
                ] as const).map(([kind, label, hint]) => (
                  <button
                    key={kind}
                    type="button"
                    className="block w-full px-3 py-2 text-left hover:bg-[var(--sc-bg)]"
                    onClick={() => downloadTemplate(kind)}
                  >
                    <span className="block text-[var(--sc-text)]">{label}</span>
                    <span className="block text-[11px] text-[var(--sc-muted)]">{hint}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={reading}>
            {reading ? "Wczytywanie…" : "Importuj z pliku"}
          </Button>
          <Button size="sm" onClick={addDraft}>Dodaj wiersz</Button>
          </>}
        </div>
      </div>

      {!!source.error && <div className="px-4 py-3 text-sm text-[#C03030]">Nie udało się pobrać pozycji.</div>}
      {source.isLoading ? (
        <div className="px-4 py-6 text-sm text-[var(--sc-muted)]">Ładowanie…</div>
      ) : (
        <div ref={wrapperRef} onPaste={onPaste}>
          <DataGrid
            className="rdg-light text-sm"
            columns={columns}
            rows={rows}
            rowKeyGetter={(r) => r.key}
            onRowsChange={readOnly ? undefined : onRowsChange}
            rowHeight={40}
            rowClass={(r) => (r.needsReview ? "bg-[#FFFBF2]" : undefined)}
            // rejestr ma kilkadziesiąt pozycji: wszystkie wiersze i kolumny w DOM, bez mierzenia widoku
            enableVirtualization={false}
            headerRowHeight={56}
            defaultColumnOptions={{ resizable: true }}
            style={{ height: Math.min(600, 54 + Math.max(rows.length, 3) * 40 + 2) }}
            renderers={{
              noRowsFallback: (
                <div className="p-4 text-sm text-[var(--sc-muted)]" style={{ gridColumn: "1/-1" }}>
                  Brak pozycji. Dodaj wiersz albo wklej wiersze z Excela.
                </div>
              ),
            }}
          />
        </div>
      )}

      {invalidDrafts > 0 && (
        <div className="px-4 py-2 text-xs text-[#C03030] border-t border-[var(--sc-border)]">
          {invalidDrafts === 1 ? "1 wiersz nie jest zapisany" : `${invalidDrafts} wiersze nie są zapisane`}: popraw komórki zaznaczone na czerwono (najedź, żeby zobaczyć powód).
        </div>
      )}

      {pasted && (
        <ActionLogPasteModal
          parsed={pasted}
          title={importTitle}
          categories={categories}
          onCancel={() => setPasted(null)}
          onImport={importPasted}
        />
      )}
    </div>
  );
};
